import { useCallback } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';

import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { verticalSortableListCollisionDetection } from './verticalSortableList';

const DraggableList = ({
  itemList,
  setState,
  updateOrder,
  children,
  renderOverlay,
  activeId,
  setActiveId,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      let updatedArray;
      // Use the functional updater to access the latest state, avoiding stale closure
      // on itemList when items are added/removed between renders.
      setState((prevList) => {
        const [activeElement] = prevList.filter(item => item.id === active.id);
        const [overElement] = prevList.filter(item => item.id === over.id);
        const oldIndex = prevList.indexOf(activeElement);
        const newIndex = prevList.indexOf(overElement);
        updatedArray = arrayMove(prevList, oldIndex, newIndex);
        return updatedArray;
      });
      updateOrder()(updatedArray);
    }
    setActiveId?.(null);
  }, [setState, updateOrder, setActiveId]);

  const handleDragStart = useCallback((event) => {
    setActiveId?.(event.active.id);
  }, [setActiveId]);

  const handleDragCancel = useCallback(() => {
    setActiveId?.(null);
  }, [setActiveId]);

  return (
    <DndContext
      sensors={sensors}
      modifiers={[restrictToVerticalAxis]}
      collisionDetection={verticalSortableListCollisionDetection}
      onDragStart={handleDragStart}
      // autoScroll does not play well with verticalSortableListCollisionDetection strategy
      autoScroll={false}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext
        items={itemList}
        strategy={verticalListSortingStrategy}
      >
        {children}
      </SortableContext>
      {renderOverlay && createPortal(
        <DragOverlay>
          {renderOverlay(activeId)}
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
  );
};

DraggableList.defaultProps = {
  renderOverlay: undefined,
  activeId: null,
  setActiveId: () => {},
};

DraggableList.propTypes = {
  itemList: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
  })).isRequired,
  setState: PropTypes.func.isRequired,
  updateOrder: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  renderOverlay: PropTypes.func,
  activeId: PropTypes.string,
  setActiveId: PropTypes.func,
};

export default DraggableList;
